import { useState, useEffect } from 'react';
import axios from 'axios';
import InvestmentForm from './InvestmentForm';
import ImportInvestmentCSV from './ImportInvestmentCSV';

const API = '/api';

function Investments() {
  const [investments, setInvestments] = useState([]);
  const [editData, setEditData] = useState(null);
  const [tab, setTab] = useState('list');
  const [showTransactions, setShowTransactions] = useState(false);
  const [filters, setFilters] = useState({ date: '', ticker: '', type: '', assetClass: '', amount: '' });

  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  const fetchInvestments = () => {
    axios.get(`${API}/investments`).then(res => setInvestments(res.data));
  };

  useEffect(() => { fetchInvestments(); }, []);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/investments/${id}`);
    fetchInvestments();
  };

  const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

  const totalBuys = investments
    .filter(t => t.type === 'buy')
    .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);

  const totalSells = investments
    .filter(t => t.type === 'sell')
    .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);

  const totalBrokerage = investments
    .reduce((sum, t) => sum + Number(t.brokerage), 0);

  const realised = totalSells - totalBuys;

  const filtered = investments.filter(t => {
    const date = new Date(t.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }).toLowerCase();
    return (
      date.includes(filters.date.toLowerCase()) &&
      (t.ticker || '').toLowerCase().includes(filters.ticker.toLowerCase()) &&
      (t.type || '').toLowerCase().includes(filters.type.toLowerCase()) &&
      (t.assetClass || '').toLowerCase().includes(filters.assetClass.toLowerCase()) &&
      String(t.amount).includes(filters.amount)
    );
  });

  // Holdings summary — group by ticker
  const holdings = {};
  investments
    .filter(t => ['buy', 'sell'].includes(t.type))
    .sort((a, b) => new Date(a.date) - new Date(b.date)) // process buys before sells
    .forEach(t => {
      if (!holdings[t.ticker]) holdings[t.ticker] = { ticker: t.ticker, assetClass: t.assetClass, units: 0, totalCost: 0, brokerage: 0 };
      if (t.type === 'buy') {
        holdings[t.ticker].units += Number(t.units);
        holdings[t.ticker].totalCost += Math.abs(Number(t.amount));
        holdings[t.ticker].brokerage += Number(t.brokerage);
      } else if (t.type === 'sell') {
        const currentUnits = holdings[t.ticker].units;
        if (currentUnits > 0) {
          const avgCost = holdings[t.ticker].totalCost / currentUnits;
          const unitsSold = Number(t.units);
          holdings[t.ticker].units -= unitsSold;
          holdings[t.ticker].totalCost -= avgCost * unitsSold;
        }
      }
    });

  const holdingsList = Object.values(holdings).filter(h => h.units > 0);

  return (
    <div>
      {/* Summary Cards */}
      <div className="summary">
        <div className="card">
          <div className="card-label">Total Invested</div>
          <div className="card-value expense">{fmt(totalBuys)}</div>
        </div>
        <div className="card">
          <div className="card-label">Total Sold</div>
          <div className="card-value income">{fmt(totalSells)}</div>
        </div>
        <div className="card">
          <div className="card-label">Brokerage Paid</div>
          <div className="card-value expense">{fmt(totalBrokerage)}</div>
        </div>
        <div className="card">
          <div className="card-label">Realised P&L</div>
          <div className={`card-value ${realised >= 0 ? 'income' : 'expense'}`}>{fmt(realised)}</div>
        </div>
      </div>

      {/* Holdings */}
      {holdingsList.length > 0 && (
        <div className="table-wrap" style={{ marginBottom: '28px' }}>
          <div className="table-header">
            <div className="table-title">Current Holdings</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Asset Class</th>
                <th>Units Held</th>
                <th>Avg Cost/Unit</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {holdingsList.map(h => (
                <tr key={h.ticker}>
                  <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: '500' }}>{h.ticker}</td>
                  <td><span className="badge" style={{ background: '#f0f9ff', color: '#0369a1' }}>{h.assetClass}</span></td>
                  <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px' }}>{Number(h.units).toFixed(4)}</td>
                  <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px' }}>{fmt(h.totalCost / h.units)}</td>
                  <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px' }}>{fmt(h.totalCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'list' ? 'active' : ''}`} onClick={() => setTab('list')}>Transactions</button>
        <button className={`tab ${tab === 'add' ? 'active' : ''}`} onClick={() => { setTab('add'); setEditData(null); }}>Add</button>
        <button className={`tab ${tab === 'import' ? 'active' : ''}`} onClick={() => setTab('import')}>Import CSV</button>
      </div>

      {/* Add / Edit Form */}
      {tab === 'add' && (
        <div className="form-wrap">
          <div className="form-title">{editData ? 'Edit Transaction' : 'New Investment Transaction'}</div>
          <InvestmentForm
            editData={editData}
            onSaved={() => { fetchInvestments(); setEditData(null); setTab('list'); }}
            onCancel={() => { setEditData(null); setTab('list'); }}
          />
        </div>
      )}

      {/* Transactions Table */}
      {tab === 'list' && (
        <div className="table-wrap">
          <div className="table-header">
            <div className="table-title">All Transactions</div>
            <button className="btn" onClick={() => setShowTransactions(prev => !prev)}>
              {showTransactions ? '▲ hide' : '▼ show'}
            </button>
          </div>
          {showTransactions && (filtered.length === 0 ? (
            <div className="empty-state">{investments.length === 0 ? 'no transactions yet' : 'no results'}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Ticker</th>
                  <th>Type</th>
                  <th>Asset Class</th>
                  <th>Units</th>
                  <th>Price/Unit</th>
                  <th>Brokerage</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
                <tr>
                  {['date', 'ticker', 'type', 'assetClass', 'amount'].map(key => (
                    <th key={key} style={{ padding: '6px 20px' }}>
                      <input
                        value={filters[key] || ''}
                        onChange={e => updateFilter(key, e.target.value)}
                        placeholder="filter..."
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border: '1px solid var(--border)',
                          borderRadius: '3px',
                          background: 'var(--surface)',
                          fontFamily: 'DM Mono, monospace',
                          fontSize: '11px',
                          color: 'var(--text)',
                          outline: 'none'
                        }}
                      />
                    </th>
                  ))}
                  <th colSpan="4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}>
                    <td className="td-date">{new Date(t.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                    <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: '500' }}>{t.ticker}</td>
                    <td><span className="badge" style={{
                      background: t.type === 'buy' ? '#fee2e2' : t.type === 'sell' ? '#dcfce7' : '#ede9fe',
                      color: t.type === 'buy' ? 'var(--expense)' : t.type === 'sell' ? 'var(--income)' : 'var(--transfer)'
                    }}>{t.type}</span></td>
                    <td><span className="badge" style={{ background: '#f0f9ff', color: '#0369a1' }}>{t.assetClass}</span></td>
                    <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px' }}>{Number(t.units).toFixed(4)}</td>
                    <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px' }}>{fmt(t.pricePerUnit)}</td>
                    <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', color: 'var(--muted)' }}>{fmt(t.brokerage)}</td>
                    <td style={{ fontFamily: 'DM Mono, monospace', color: Number(t.amount) < 0 ? 'var(--expense)' : 'var(--income)' }}>
                      {fmt(Number(t.amount))}
                    </td>
                    <td>
                      <div className="btn-row">
                        <button className="btn btn-edit" onClick={() => { setEditData(t); setTab('add'); }}>edit</button>
                        <button className="btn btn-delete" onClick={() => handleDelete(t.id)}>del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="7" style={{ padding: '12px 20px', fontFamily: 'DM Mono, monospace', fontSize: '11px', letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--muted)', borderTop: '2px solid var(--border)' }}>
                    {filtered.length} transactions
                  </td>
                  <td style={{ padding: '12px 20px', fontFamily: 'DM Mono, monospace', fontWeight: '500', borderTop: '2px solid var(--border)', color: (() => {
                    const total = filtered.reduce((sum, t) => sum + Number(t.amount), 0);
                    return total < 0 ? 'var(--expense)' : 'var(--income)';
                  })() }}>
                    {fmt(filtered.reduce((sum, t) => sum + Number(t.amount), 0))}
                  </td>
                  <td style={{ borderTop: '2px solid var(--border)' }}></td>
                </tr>
              </tfoot>
            </table>
          ))}
        </div>
      )}

      {tab === 'import' && (
        <ImportInvestmentCSV onImported={() => { fetchInvestments(); setTab('list'); }} />
      )}
    </div>
  );
}

export default Investments;